interface OneKeyInsightContent {
  insight?: string;
  chips?: Array<{ id: string; text: string }>;
}

interface OneKeyInsightEditorEnhancedProps {
  content: OneKeyInsightContent;
  onChange: (content: OneKeyInsightContent) => void;
  disabled?: boolean;
  civicLevel?: string;
}

export default function OneKeyInsightEditorEnhanced({
  content,
  onChange,
  disabled = false,
  civicLevel = 'observer',
}: OneKeyInsightEditorEnhancedProps) {
  const isExplorer = civicLevel.toLowerCase() === 'explorer';

  // Explorer mode: 3 insight chips
  if (isExplorer) {
    const chips = content.chips || [
      { id: 'chip1', text: '' },
      { id: 'chip2', text: '' },
      { id: 'chip3', text: '' },
    ];

    const handleChipChange = (index: number, text: string) => {
      const newChips = [...chips];
      newChips[index] = { ...newChips[index], text };
      onChange({ ...content, chips: newChips });
    };

    const getWordCount = (text: string) => {
      return text.trim().split(/\s+/).filter(w => w.length > 0).length;
    };

    return (
      <div className="space-y-6">
        {/* Instructions */}
        <div className="bg-[#1A1A1A] border border-[#282828] rounded-lg p-4">
          <p className="text-[13px] text-[#A0A0A0] leading-relaxed">
            For Explorer level, provide 3 distinct insights. Each should be 8-18 words, globally readable, and offer a different lens.
          </p>
        </div>

        {/* Chip Inputs */}
        {chips.map((chip, index) => {
          const wordCount = getWordCount(chip.text);
          const isInRange = wordCount >= 8 && wordCount <= 18;
          const isEmpty = wordCount === 0;

          return (
            <div key={chip.id}>
              <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                Insight {index + 1}
              </label>
              <textarea
                value={chip.text}
                onChange={(e) => handleChipChange(index, e.target.value)}
                disabled={disabled}
                rows={2}
                placeholder={`Insight ${index + 1}: A clear, accessible observation (8-18 words)`}
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[14px] text-[#F5F5F5] placeholder:text-[#606060] leading-relaxed resize-vertical focus:border-[#40E0D0]/30 focus:outline-none disabled:opacity-50 transition-colors"
              />
              <div className="flex items-center justify-between mt-2">
                <p className="text-[11px] text-[#606060]">
                  8–18 words
                </p>
                <p
                  className={`text-[11px] font-medium ${
                    isEmpty
                      ? 'text-[#606060]'
                      : isInRange
                      ? 'text-[#40E0D0]'
                      : 'text-[#FFB86C]'
                  }`}
                >
                  {wordCount} word{wordCount !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Default mode (Observer and other levels): single insight text
  const charCount = content.insight?.length || 0;
  const recommended = 400;
  const isOverRecommended = charCount > recommended;

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-[13px] font-medium text-[#A0A0A0] mb-3">
          Key Insight Text
        </label>
        <textarea
          value={content.insight || ''}
          onChange={(e) => onChange({ ...content, insight: e.target.value })}
          disabled={disabled}
          rows={5}
          placeholder="A single powerful insight or perspective shift that this song offers. Keep it concise and impactful."
          className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[14px] text-[#F5F5F5] placeholder:text-[#606060] leading-relaxed resize-vertical focus:border-[#40E0D0]/30 focus:outline-none disabled:opacity-50 transition-colors"
        />
      </div>

      <div className="flex items-center justify-between text-[11px]">
        <p className="text-[#606060]">
          Recommended: 240-400 characters for optimal readability
        </p>
        <p
          className={`font-medium ${
            isOverRecommended ? 'text-[#FFB86C]' : 'text-[#808080]'
          }`}
        >
          {charCount} / {recommended}
        </p>
      </div>
    </div>
  );
}

export type { OneKeyInsightContent };
